import { apiService } from './api';

export interface Unit {
  unitId: number;
  unitCode: string;
  unitName: string;
  unitShortName: string;
  unitCategory: string;
  decimalAllowed: boolean;
  isBaseUnit: boolean;
  description?: string;
  isActive: boolean;
  createdDate: string;
}

export interface CreateUnitDTO {
  // unitCode is auto-generated by the server — not sent from the client
  unitName: string;
  unitShortName: string;
  unitCategory: string;
  decimalAllowed: boolean;
  isBaseUnit: boolean;
  description?: string;
}

export interface UpdateUnitDTO {
  // unitCode is immutable — auto-generated on create, never updated
  unitName: string;
  unitShortName: string;
  unitCategory: string;
  decimalAllowed: boolean;
  isBaseUnit: boolean;
  description?: string;
  isActive: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

class UnitService {
  async getUnits(pageNumber: number = 1, pageSize: number = 50) {
    try {
      const response = await apiService.get<PaginatedResponse<Unit>>(
        '/units',
        { pageNumber, pageSize }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching units:', error);
      throw error;
    }
  }

  async getUnitById(id: number) {
    try {
      const response = await apiService.get<Unit>(`/units/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching unit:', error);
      throw error;
    }
  }

  async getUnitByCode(code: string) {
    try {
      const response = await apiService.get<Unit>(`/units/code/${code}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching unit by code:', error);
      throw error;
    }
  }

  async getUnitsByCategory(category: string) {
    try {
      const response = await apiService.get<Unit[]>(`/units/category/${category}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching units by category:', error);
      throw error;
    }
  }

  async createUnit(dto: CreateUnitDTO) {
    try {
      const response = await apiService.post<Unit>('/units', dto);
      return response.data.data;
    } catch (error) {
      console.error('Error creating unit:', error);
      throw error;
    }
  }

  async updateUnit(id: number, dto: UpdateUnitDTO) {
    try {
      const response = await apiService.put<Unit>(`/units/${id}`, dto);
      return response.data.data;
    } catch (error) {
      console.error('Error updating unit:', error);
      throw error;
    }
  }

  async deleteUnit(id: number) {
    try {
      await apiService.delete(`/units/${id}`);
    } catch (error) {
      console.error('Error deleting unit:', error);
      throw error;
    }
  }
}

const unitService = new UnitService();
export default unitService;
